import React from 'react';
import mark from '../../assets/brand/mark.png';

/**
 * Shared section furniture. Tone decides the ground, and everything that
 * sits on that ground takes its colour from the same set, so no block ever
 * ends up light-on-light or dark-on-dark.
 */
const TONES = {
  paper: 'bg-paper text-soil',
  chalk: 'bg-chalk text-soil',
  paper2: 'bg-paper2 text-soil',
  ink: 'bg-ink text-paper',
  soil: 'bg-soil text-paper',
  moss: 'bg-moss text-paper',
};

export const isDark = (tone) => ['ink', 'soil', 'moss'].includes(tone);

export const Section = ({ tone = 'paper', className = '', id, children, tight = false }) => (
  <section id={id} className={`${TONES[tone]} ${tight ? 'py-14' : 'py-20 md:py-28'} ${className}`}>
    <div className="container mx-auto px-5 md:px-8">{children}</div>
  </section>
);

/** Eyebrow label. The leaf mark is the Annpriya glyph, not decoration. */
export const Eyebrow = ({ children, tone = 'light', className = '' }) => (
  <p
    className={`label flex items-center gap-2.5 ${
      tone === 'dark' ? 'text-lime' : 'text-forest'
    } ${className}`}
  >
    <img src={mark} alt="" aria-hidden="true" className="w-3.5 h-3.5 shrink-0" />
    {children}
  </p>
);

export const H2 = ({ children, className = '' }) => (
  <h2 className={`font-display font-semibold text-[2.1rem] leading-[1.12] md:text-[3rem] ${className}`}>
    {children}
  </h2>
);

export const Lead = ({ children, tone = 'light', className = '' }) => (
  <p
    className={`text-[1.0625rem] md:text-lg leading-relaxed ${
      tone === 'dark' ? 'text-paper/70' : 'text-soil/70'
    } ${className}`}
  >
    {children}
  </p>
);

/** Section head: eyebrow, title, optional lead. */
export const Head = ({ eyebrow, title, lead, tone = 'light', center = false, className = '' }) => (
  <div className={`${center ? 'text-center mx-auto max-w-3xl' : 'max-w-3xl'} ${className}`}>
    {eyebrow && (
      <Eyebrow tone={tone === 'dark' ? 'dark' : 'light'} className={center ? 'justify-center' : ''}>
        {eyebrow}
      </Eyebrow>
    )}
    <H2 className={`mt-4 ${tone === 'dark' ? 'text-paper' : 'text-ink'}`}>{title}</H2>
    {lead && <Lead tone={tone} className="mt-5">{lead}</Lead>}
  </div>
);

/** A numeral that reads as an index, not a decoration. */
export const Index = ({ n, tone = 'light', accent = false }) => (
  <span
    className={`font-mono text-[11px] font-semibold shrink-0 w-7 h-7 rounded-full grid place-items-center ${
      accent
        ? 'bg-amber text-ink'
        : tone === 'dark'
        ? 'bg-lime/15 text-lime ring-1 ring-lime/30'
        : 'bg-forest/10 text-forest ring-1 ring-forest/20'
    }`}
  >
    {String(n).padStart(2, '0')}
  </span>
);

/** Hairline card. */
export const Card = ({ tone = 'light', className = '', children }) => (
  <div
    className={`rounded-xl ${
      tone === 'dark'
        ? 'bg-white/[0.04] ring-1 ring-white/10'
        : 'bg-chalk ring-1 ring-soil/10'
    } ${className}`}
  >
    {children}
  </div>
);

/** Key/value spec row - the mono key is what makes it read as a datasheet. */
export const Spec = ({ k, v, tone = 'light' }) => (
  <div
    className={`flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-6 py-3 border-b ${
      tone === 'dark' ? 'border-white/10' : 'border-soil/10'
    }`}
  >
    <dt className={`label sm:w-44 shrink-0 ${tone === 'dark' ? 'text-lime/80' : 'text-forest'}`}>{k}</dt>
    <dd className={`text-[0.95rem] ${tone === 'dark' ? 'text-paper/80' : 'text-soil/80'}`}>{v}</dd>
  </div>
);

export const Pill = ({ children, tone = 'light', accent = false }) => (
  <span
    className={`inline-flex items-center rounded-full px-3.5 py-1.5 text-[13px] font-medium ${
      accent
        ? 'bg-amber/15 text-amber ring-1 ring-amber/30'
        : tone === 'dark'
        ? 'bg-lime/10 text-lime ring-1 ring-lime/25'
        : 'bg-forest/8 text-forest ring-1 ring-forest/15'
    }`}
  >
    {children}
  </span>
);

/**
 * The image half of a two-column section.
 *
 * Below `lg` it is a normal image at its own aspect ratio. From `lg` up the
 * picture is taken out of flow, so the row's height is set by the text column
 * alone and the image fills exactly that, so a tall portrait can no longer tower
 * over the words beside it. `lg:min-h-[18rem]` is a floor for the cases where
 * the text is very short.
 */
export const SideImage = ({ src, alt, tone = 'light', className = '', children }) => (
  <div className={`relative lg:h-full lg:min-h-[18rem] ${className}`}>
    <img
      src={src}
      alt={alt}
      className={`w-full h-full object-cover rounded-xl ring-1 lg:absolute lg:inset-0 ${
        tone === 'dark' ? 'ring-white/10' : 'ring-soil/10'
      }`}
    />
    {children}
  </div>
);

/**
 * A flow connector with a real shaft.
 *
 * lucide's arrows are drawn inside a 24x24 box where the head takes most of
 * the room, so at any size they read as a link glyph rather than a connector
 * between two things. This one keeps a 2px shaft of whatever length the layout
 * can give it and puts the head at the far end, so the direction is legible
 * from across the page.
 *
 * `length` is measured along the direction of travel; the cross-axis is fixed
 * at 32px with the shaft on its centre line, which is what lets a caller centre
 * the arrow on a row or a gap with a plain -translate.
 */
export const FlowArrow = ({ dir = 'down', length = 76, className = '' }) => {
  const vertical = dir === 'down' || dir === 'up';
  const forward = dir === 'down' || dir === 'right';

  /* Positions along the direction of travel. The shaft stops short of the tip
     so the round cap does not poke through the join of the two head strokes. */
  const tail = forward ? 2 : length - 2;
  const shaftEnd = forward ? length - 12 : 12;
  const barb = forward ? length - 13 : 13;
  const tip = forward ? length - 3 : 3;

  /* Vertical arrows are the same path with the two axes swapped. */
  const at = (along, across) => (vertical ? `${across} ${along}` : `${along} ${across}`);

  return (
    <svg
      width={vertical ? 32 : length}
      height={vertical ? length : 32}
      viewBox={`0 0 ${vertical ? 32 : length} ${vertical ? length : 32}`}
      fill="none"
      className={`shrink-0 ${className}`}
      aria-hidden="true"
    >
      <path
        d={`M${at(tail, 16)} L${at(shaftEnd, 16)} M${at(barb, 9)} L${at(tip, 16)} L${at(barb, 23)}`}
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};
